// Pure helpers behind the Hooks page's course list: per-course counts
// from the flat hook rows, the quick filters, their sort orders, and the
// "next course needing approval" step. Kept free of React so they can be
// tested directly.

export const FILTERS = {
    NEEDS_APPROVAL: 'needs',
    NO_APPROVED: 'none',
    ALL: 'all',
};

export const FILTER_OPTIONS = [
    { label: 'Needs approval', value: FILTERS.NEEDS_APPROVAL },
    { label: 'No approved hook', value: FILTERS.NO_APPROVED },
    { label: 'All courses', value: FILTERS.ALL },
];

// One row per published course (plus `pinnedId`, whatever its status, so
// a ?course= deep link always resolves), with its hook counts.
export function buildCourseRows(courses, hooks, pinnedId = null) {
    const counts = {};
    for (const hook of hooks) {
        const c = counts[hook.course_id] || (counts[hook.course_id] = { approved: 0, draft: 0 });
        if (hook.status === 'approved') c.approved += 1;
        else c.draft += 1;
    }
    return courses
        .filter(course => course.status === 'published' || course.id === pinnedId)
        .map(course => {
            const c = counts[course.id] || { approved: 0, draft: 0 };
            return {
                id: course.id,
                name: course.name,
                status: course.status,
                categoryIds: course.categories || [],
                doNotMiss: !!course.do_not_miss,
                approved: c.approved,
                draft: c.draft,
                total: c.approved + c.draft,
                needsApproval: c.draft > 0,
                noApproved: c.approved === 0,
            };
        });
}

const byName = (a, b) => a.name.localeCompare(b.name);
const byDraftsThenName = (a, b) => (b.draft - a.draft) || byName(a, b);

// Rows matching `filter`, the name `query` and, when set, `categoryId`
// and the do-not-miss flag, in that filter's order. The pinned course is
// always included, at its sorted place.
export function filterCourseRows(rows, {
    filter, query = '', categoryId = null, doNotMissOnly = false, pinnedId = null,
}) {
    const q = query.trim().toLowerCase();
    const matches = (row) => {
        if (row.id === pinnedId) return true;
        if (q && !row.name.toLowerCase().includes(q)) return false;
        if (categoryId != null && !row.categoryIds.includes(categoryId)) return false;
        if (doNotMissOnly && !row.doNotMiss) return false;
        if (filter === FILTERS.NEEDS_APPROVAL) return row.needsApproval;
        if (filter === FILTERS.NO_APPROVED) return row.noApproved;
        return true;
    };
    const sorted = filter === FILTERS.ALL ? byName : byDraftsThenName;
    return rows.filter(matches).sort(sorted);
}

// "X of N published courses have an approved hook".
export function coverage(rows) {
    const published = rows.filter(r => r.status === 'published');
    return { covered: published.filter(r => !r.noApproved).length, total: published.length };
}

// The course to move to once the current one has no drafts left: the
// next row with drafts in the needs-approval order, wrapping around;
// null when no course needs approval any more.
export function nextCourseNeedingApproval(rows, currentId) {
    const queue = rows.filter(r => r.needsApproval && r.id !== currentId).sort(byDraftsThenName);
    if (queue.length === 0) return null;
    const ordered = rows.filter(r => r.needsApproval || r.id === currentId).sort(byDraftsThenName);
    const at = ordered.findIndex(r => r.id === currentId);
    const after = ordered.slice(at + 1).find(r => r.needsApproval);
    return after || queue[0];
}

// The order the app uses: a reordered set of approved hooks, followed by
// the drafts in their current order — the exact permutation the order
// endpoint requires.
export function orderWithDraftsLast(approvedInNewOrder, allHooks) {
    const approvedIds = new Set(approvedInNewOrder.map(h => h.id));
    const drafts = allHooks.filter(h => !approvedIds.has(h.id));
    return [...approvedInNewOrder, ...drafts].map(h => h.id);
}
